import React from "react";
import {
  Card,
  CardContent,
  Chip,
  Box,
  Tooltip,
  Typography,
  IconButton,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { alpha, useTheme } from "@mui/material/styles";
import { useColorMode } from "../../../context/ColorModeContext";
import {
  Visibility,
  Edit as EditIcon,
  Delete as DeleteIcon,
  HourglassEmpty as PendingIcon,
  PendingActions as PendingActionsIcon,
  CheckCircleOutline as SignedIcon,
  CloudDone as SubmittedIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { ReportGenReport } from "../../../shared/interfaces/ReportGenReport";
import { ReportStatus } from "../../../shared/interfaces/ReportStatus";
import { NavigateFunction } from "react-router-dom";
import {
  AWAITING_INPUT_BASE,
  getAwaitingInputChipSx,
  getAwaitingInputSurfaceStyles,
} from "../../../utils/awaitingInputStyles";
import {
  getAwaitingInputActionLabel,
  getAwaitingInputRoute,
} from "../../../features/hitl/presentPlan";
import { useHitlEnabled } from "../../../hooks/useHitlEnabled";

interface ReportCardProps {
  report: ReportGenReport;
  navigate: NavigateFunction;
  onDelete: (report: ReportGenReport) => void;
}

export const ReportCard: React.FC<ReportCardProps> = ({
  report,
  navigate,
  onDelete,
}) => {
  const theme = useTheme();
  const { mode } = useColorMode();
  const isDark = mode === "dark";
  const hitlEnabled = useHitlEnabled();

  const APPROVED_STATUSES = [
    ReportStatus.APPROVED,
    ReportStatus.SIGNED,
    ReportStatus.RMS_COMMITTED,
  ];
  const isAwaitingInput = report.status === ReportStatus.AWAITING_INPUT;
  const awaitingInputSurfaceStyles = getAwaitingInputSurfaceStyles(theme);
  const awaitingInputRoute = getAwaitingInputRoute(report.id, hitlEnabled);
  const awaitingInputActionLabel = getAwaitingInputActionLabel(hitlEnabled);

  return (
    <Card
      variant="outlined"
      sx={{
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        cursor: "pointer",
        borderRadius: 2,
        borderColor: isDark
          ? alpha(theme.palette.text.primary, 0.2)
          : alpha(theme.palette.divider, 0.4),
        ...(isAwaitingInput && {
          backgroundColor: awaitingInputSurfaceStyles.backgroundColor,
          borderColor: awaitingInputSurfaceStyles.borderColor,
        }),
        "&:hover": {
          boxShadow: isAwaitingInput
            ? `0 8px 25px ${alpha(AWAITING_INPUT_BASE, 0.18)}`
            : `0 8px 25px ${alpha(theme.palette.primary.main, 0.15)}`,
          borderColor: isAwaitingInput
            ? awaitingInputSurfaceStyles.borderColor
            : theme.palette.primary.main,
          transform: "translateY(-3px)",
          "& .card-actions": {
            opacity: 1,
          },
        },
        "&:active": {
          transform: "translateY(-1px)",
        },
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          borderRadius: "4px 0 0 4px",
          ...(report.status === "PENDING REVIEW" && {
            bgcolor: "warning.main",
          }),
          ...(report.status === "AWAITING INPUT" && {
            bgcolor: AWAITING_INPUT_BASE,
          }),
          ...(report.status === "SIGNED" && {
            bgcolor: "info.main",
          }),
          ...(report.status === "RMS COMMITTED" && {
            bgcolor: "success.main",
          }),
        },
      }}
      onClick={() =>
        report.status === ReportStatus.PENDING_REVIEW
          ? navigate(`/edit-section/${report.id}`)
          : report.status === ReportStatus.AWAITING_INPUT
            ? navigate(awaitingInputRoute)
          : navigate(`/preview-report-info/${report.id}`)
      }
    >
      <CardContent sx={{ p: 1.5, pl: 2.5, "&:last-child": { pb: 1.5 } }}>
        <Grid container spacing={2} alignItems="center">
          {/* Main Info Section */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Box display="flex" alignItems="center" gap={1} mb={1.5}>
              <Chip
                icon={
                  report.status === ReportStatus.PENDING_REVIEW ? (
                    <PendingIcon sx={{ fontSize: "0.9rem !important" }} />
                  ) : report.status === ReportStatus.AWAITING_INPUT ? (
                    <PendingActionsIcon sx={{ fontSize: "0.9rem !important" }} />
                  ) : report.status === "SIGNED" ? (
                    <SignedIcon sx={{ fontSize: "0.9rem !important" }} />
                  ) : report.status === ReportStatus.RMS_COMMITTED ? (
                    <SubmittedIcon sx={{ fontSize: "0.9rem !important" }} />
                  ) : undefined
                }
                label={
                  report.status === ReportStatus.RMS_COMMITTED
                    ? "SUBMITTED"
                    : report.status === ReportStatus.AWAITING_INPUT
                      ? "AWAITING INPUT"
                      : report.status
                }
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  height: 26,
                  borderRadius: 1.5,
                  ...(report.status === ReportStatus.PENDING_REVIEW && {
                    backgroundColor: alpha(theme.palette.warning.main, 0.15),
                    color: theme.palette.warning[isDark ? "light" : "dark"],
                    "& .MuiChip-icon": { color: "inherit" },
                  }),
                  ...(report.status === ReportStatus.AWAITING_INPUT && {
                    ...getAwaitingInputChipSx(),
                  }),
                  ...(report.status === ReportStatus.SIGNED && {
                    backgroundColor: alpha(theme.palette.info.main, 0.15),
                    color: theme.palette.info[isDark ? "light" : "dark"],
                    "& .MuiChip-icon": { color: "inherit" },
                  }),
                  ...(report.status === ReportStatus.RMS_COMMITTED && {
                    backgroundColor: alpha(theme.palette.success.main, 0.15),
                    color: theme.palette.success[isDark ? "light" : "dark"],
                    "& .MuiChip-icon": { color: "inherit" },
                  }),
                }}
              />
            </Box>

            <Tooltip
              title={report.details || "No details available"}
              placement="bottom-start"
              enterDelay={500}
            >
              <Typography
                variant="h6"
                component="div"
                sx={{
                  fontSize: "1.05rem",
                  fontWeight: 600,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  lineHeight: 1.4,
                  color: "text.primary",
                }}
              >
                {report.details || (
                  <Box
                    component="span"
                    sx={{
                      color: "text.disabled",
                      fontStyle: "italic",
                    }}
                  >
                    No details available
                  </Box>
                )}
              </Typography>
            </Tooltip>
          </Grid>

          {/* Reported By Section */}
          <Grid size={{ xs: 6, md: 3 }}>
            <Box
              display="flex"
              flexDirection="column"
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.grey[500], isDark ? 0.15 : 0.08),
                transition: "background-color 0.2s ease",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: "0.65rem",
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                  fontWeight: 600,
                  mb: 0.5,
                }}
              >
                Reported By
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                noWrap
                title={report.reportedByName}
                sx={{
                  color: "text.primary",
                  lineHeight: 1.3,
                }}
              >
                {report.reportedByName}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ fontSize: "0.75rem" }}
              >
                {report.reportedByPosition}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.8rem",
                  mt: 0.25,
                }}
              >
                {dayjs(report.reportedByDate).format("MMM D, YYYY · h:mm A")}
              </Typography>
            </Box>
          </Grid>

          {/* Modified By Section */}
          <Grid size={{ xs: 6, md: 3 }}>
            <Box
              display="flex"
              flexDirection="column"
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.grey[500], isDark ? 0.15 : 0.08),
                transition: "background-color 0.2s ease",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: "0.65rem",
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                  fontWeight: 600,
                  mb: 0.5,
                }}
              >
                Last Modified
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                noWrap
                title={report.modifiedByName}
                sx={{
                  color: "text.primary",
                  lineHeight: 1.3,
                }}
              >
                {report.modifiedByName}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.8rem",
                  mt: 0.25,
                }}
              >
                {dayjs(report.dateModified).format("MMM D, YYYY · h:mm A")}
              </Typography>
            </Box>
          </Grid>

          {/* Actions Section */}
          <Grid size={{ xs: 12, md: 1 }}>
            {/* Desktop View */}
            <Box
              className="card-actions"
              display={{ xs: "none", md: "flex" }}
              flexDirection="column"
              justifyContent="center"
              gap={1}
              onClick={(e) => e.stopPropagation()}
              sx={{
                opacity: 0.7,
                transition: "opacity 0.2s ease",
              }}
            >
              <Tooltip
                title={
                  APPROVED_STATUSES.includes(report.status)
                    ? "View Report"
                    : isAwaitingInput
                      ? awaitingInputActionLabel
                      : "Edit Report"
                }
                placement="left"
              >
                <IconButton
                  size="small"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(
                      report.status === ReportStatus.PENDING_REVIEW
                        ? `/edit-section/${report.id}`
                        : report.status === ReportStatus.AWAITING_INPUT
                          ? awaitingInputRoute
                        : `/preview-report-info/${report.id}`
                    );
                  }}
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: "primary.main",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: "primary.main",
                      color: "white",
                      transform: "scale(1.1)",
                    },
                    width: 36,
                    height: 36,
                  }}
                >
                  {APPROVED_STATUSES.includes(report.status) ? (
                    <Visibility fontSize="small" />
                  ) : isAwaitingInput ? (
                    <PendingActionsIcon fontSize="small" />
                  ) : (
                    <EditIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
              {!APPROVED_STATUSES.includes(report.status) && (
                <Tooltip title="Delete Report" placement="left">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(report);
                    }}
                    sx={{
                      bgcolor: alpha(theme.palette.error.main, 0.1),
                      color: "error.main",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        bgcolor: "error.main",
                        color: "white",
                        transform: "scale(1.1)",
                      },
                      width: 36,
                      height: 36,
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {/* Mobile View */}
            <Box
              display={{ xs: "flex", md: "none" }}
              justifyContent="flex-end"
              gap={1}
              onClick={(e) => e.stopPropagation()}
            >
              <IconButton
                size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(
                      report.status === ReportStatus.PENDING_REVIEW
                        ? `/edit-section/${report.id}`
                        : report.status === ReportStatus.AWAITING_INPUT
                          ? awaitingInputRoute
                        : `/preview-report-info/${report.id}`
                    );
                  }}
                >
                {APPROVED_STATUSES.includes(report.status) ? (
                  <Visibility fontSize="small" />
                ) : isAwaitingInput ? (
                  <PendingActionsIcon fontSize="small" />
                ) : (
                  <EditIcon fontSize="small" />
                )}
              </IconButton>
              {!APPROVED_STATUSES.includes(report.status) && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(report);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
